import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, ClipboardList, X, Filter, ChevronDown } from 'lucide-react';

const ACTION_COLORS = {
  created: 'bg-green-500/20 text-green-400',
  updated: 'bg-blue-500/20 text-blue-400',
  deleted: 'bg-red-500/20 text-red-400',
  duplicate_resolved: 'bg-amber-500/20 text-amber-400',
};

const ACTION_LABELS = {
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
  duplicate_resolved: 'Duplicate Resolved',
};

function formatUKDate(isoStr) {
  if (!isoStr) return '-';
  try {
    const d = new Date(isoStr);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  } catch { return isoStr.slice(0, 16); }
}

function buildDescription(entry) {
  const who = entry.performed_by_name || 'Unknown';
  const role = entry.performed_by_role || '';
  const emp = entry.employee_name || 'Unknown';
  const proj = entry.project_ref || '';
  const date = entry.date || '';

  if (entry.action === 'created') return `${role === 'surveyor' ? 'Surveyor' : 'Admin'} ${who} assigned ${emp} to ${proj} on ${date}`;
  if (entry.action === 'deleted') return `${role === 'surveyor' ? 'Surveyor' : 'Admin'} ${who} removed ${emp} from ${proj} on ${date}`;
  if (entry.action === 'updated') return `${role === 'surveyor' ? 'Surveyor' : 'Admin'} ${who} updated ${emp}'s assignment on ${proj} (${date})`;
  if (entry.action === 'duplicate_resolved') {
    if (entry.duplicate_resolution === 'moved') return `${who} moved ${emp} to ${proj} on ${date} (removed from previous)`;
    if (entry.duplicate_resolution === 'kept_both') return `${who} kept ${emp} on dual shift including ${proj} on ${date}`;
  }
  return `${who} ${entry.action} ${emp} on ${proj}`;
}

export default function ScheduleChanges() {
  const { api, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [users, setUsers] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [expanded, setExpanded] = useState({});

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [performedBy, setPerformedBy] = useState('all');
  const [projectId, setProjectId] = useState('all');
  const [employeeId, setEmployeeId] = useState('all');
  const [action, setAction] = useState('all');
  const [dupResolution, setDupResolution] = useState('all');

  useEffect(() => {
    if (!isAdmin) { navigate('/portal'); return; }
    api.get('/staff').then(r => setAllEmployees(r.data || [])).catch(() => {});
    api.get('/projects?limit=200').then(r => setAllProjects(r.data || [])).catch(() => {});
  }, [isAdmin, navigate, api]);

  const buildParams = useCallback((offset = 0) => {
    const p = new URLSearchParams();
    if (dateFrom) p.set('date_from', dateFrom);
    if (dateTo) p.set('date_to', dateTo);
    if (performedBy !== 'all') p.set('performed_by', performedBy);
    if (projectId !== 'all') p.set('project_id', projectId);
    if (employeeId !== 'all') p.set('employee_id', employeeId);
    if (action !== 'all') p.set('action', action);
    if (dupResolution !== 'all') p.set('duplicate_resolution', dupResolution);
    p.set('limit', '100');
    p.set('offset', String(offset));
    return p.toString();
  }, [dateFrom, dateTo, performedBy, projectId, employeeId, action, dupResolution]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/schedules/audit-log?${buildParams(0)}`);
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  }, [api, buildParams]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await api.get(`/schedules/audit-log?${buildParams(entries.length)}`);
      setEntries(prev => [...prev, ...(res.data.entries || [])]);
    } catch { toast.error('Failed to load more'); }
    finally { setLoadingMore(false); }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const clearFilters = () => {
    setDateFrom(''); setDateTo(''); setPerformedBy('all'); setProjectId('all');
    setEmployeeId('all'); setAction('all'); setDupResolution('all');
  };
  const hasFilters = dateFrom || dateTo || performedBy !== 'all' || projectId !== 'all' || employeeId !== 'all' || action !== 'all' || dupResolution !== 'all';

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="schedule-changes-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-[#F5A623]" /> Schedule Changes
        </h1>
        <p className="text-sm text-gray-400">{total} audit entr{total !== 1 ? 'ies' : 'y'}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6 items-end">
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-[150px] bg-[#2A3240] border-white/20 text-white text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            <SelectItem value="all" className="text-white">All Actions</SelectItem>
            <SelectItem value="created" className="text-white">Created</SelectItem>
            <SelectItem value="updated" className="text-white">Updated</SelectItem>
            <SelectItem value="deleted" className="text-white">Deleted</SelectItem>
            <SelectItem value="duplicate_resolved" className="text-white">Duplicate Resolved</SelectItem>
          </SelectContent>
        </Select>
        {action === 'duplicate_resolved' && (
          <Select value={dupResolution} onValueChange={setDupResolution}>
            <SelectTrigger className="w-[130px] bg-[#2A3240] border-white/20 text-white text-xs h-9"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#2A3240] border-white/10">
              <SelectItem value="all" className="text-white">All</SelectItem>
              <SelectItem value="moved" className="text-white">Moved</SelectItem>
              <SelectItem value="kept_both" className="text-white">Kept Both</SelectItem>
            </SelectContent>
          </Select>
        )}
        <Select value={performedBy} onValueChange={setPerformedBy}>
          <SelectTrigger className="w-[150px] bg-[#2A3240] border-white/20 text-white text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            <SelectItem value="all" className="text-white">All Users</SelectItem>
            {allEmployees.filter(e => e.role === 'admin' || e.user_type === 'admin' || e.user_type === 'surveyor').map(e => (
              <SelectItem key={e.id} value={e.id} className="text-white">{e.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={employeeId} onValueChange={setEmployeeId}>
          <SelectTrigger className="w-[150px] bg-[#2A3240] border-white/20 text-white text-xs h-9"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            <SelectItem value="all" className="text-white">All Employees</SelectItem>
            {allEmployees.map(e => (
              <SelectItem key={e.id} value={e.id} className="text-white">{e.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-[130px] bg-[#2A3240] border-white/20 text-white text-xs h-9" />
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-[130px] bg-[#2A3240] border-white/20 text-white text-xs h-9" />
        {hasFilters && (
          <button onClick={clearFilters} className="text-xs text-[#F5A623] hover:underline flex items-center gap-1">
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" /></div>
      ) : entries.length === 0 ? (
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="py-12 text-center">
            {hasFilters ? (
              <>
                <Filter className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No entries match these filters</p>
                <button onClick={clearFilters} className="text-[#F5A623] text-sm mt-2 hover:underline">Clear filters</button>
              </>
            ) : (
              <>
                <ClipboardList className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No schedule changes recorded yet</p>
                <p className="text-gray-500 text-xs mt-1">Changes will appear here when schedules are created, updated, or deleted via the portal</p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {entries.map(entry => (
            <Card key={entry.id} className="bg-[#2A3240] border-white/5">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Badge className={`${ACTION_COLORS[entry.action]} text-[10px] flex-shrink-0 mt-0.5`}>
                    {ACTION_LABELS[entry.action] || entry.action}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm">{buildDescription(entry)}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                      <span>{entry.performed_by_name} ({entry.performed_by_role})</span>
                      <Badge className="bg-gray-500/20 text-gray-400 text-[9px]">{entry.performed_via === 'portal' ? 'Portal' : 'Admin'}</Badge>
                      <span>{formatUKDate(entry.created_at)}</span>
                    </div>
                    {entry.duplicate_resolution === 'kept_both' && entry.duplicate_note && (
                      <p className="text-amber-400 text-xs italic mt-2 bg-amber-500/5 p-2 rounded border border-amber-500/20">
                        Note: {entry.duplicate_note}
                      </p>
                    )}
                    {(entry.action === 'updated' || entry.action === 'deleted') && entry.previous_state && (
                      <button
                        onClick={() => setExpanded(prev => ({ ...prev, [entry.id]: !prev[entry.id] }))}
                        className="text-[#F5A623] text-xs mt-1 flex items-center gap-1 hover:underline"
                      >
                        <ChevronDown className={`w-3 h-3 transition-transform ${expanded[entry.id] ? 'rotate-180' : ''}`} />
                        {expanded[entry.id] ? 'Hide' : 'Show'} details
                      </button>
                    )}
                    {expanded[entry.id] && (
                      <div className="mt-2 text-xs bg-[#1E2530] p-2 rounded border border-white/10">
                        {entry.previous_state && (
                          <div>
                            <span className="text-gray-400">Before: </span>
                            <span className="text-gray-300">
                              project={entry.previous_state.project_id}, employees=[{(entry.previous_state.employee_ids || []).join(', ')}]
                            </span>
                          </div>
                        )}
                        {entry.new_state && (
                          <div className="mt-1">
                            <span className="text-gray-400">After: </span>
                            <span className="text-gray-300">
                              project={entry.new_state.project_id}, employees=[{(entry.new_state.employee_ids || []).join(', ')}]
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {entries.length < total && (
            <Button onClick={loadMore} disabled={loadingMore} variant="outline" className="w-full border-white/10 text-gray-400 hover:bg-white/5">
              {loadingMore ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Load more ({total - entries.length} remaining)
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
